// Standard Library
// 3rd Party
// Local

/**
 * Mixin for URI Aware Components.
 * @param {object} Base - The class to mixin to.
 * @class UriAwareMixin
 */
const UriAwareMixin = (Base) =>
  class extends Base {
    /**
     * URI configuration map used to parse and create URI strings.
     * @memberof UriAwareMixin
     * @type {object}
     */
    get configMap() {
      return {
        regex_anchor_clean1: /^[#!]*/,
        regex_anchor_clean2: /\?[^?]*$/,
        delimit_char: '&',
        delimit_kv_char: '=',
        sub_delimit_char: ':',
        dep_delimit_char: '|',
        dep_kv_delimit_char: ',',
      };
    }

    /**
     * Sets the elements URI attr/prop value.
     * @memberof UriAwareMixin
     */
    set uri(newUri) {
      this.setAttribute('uri', newUri);
    }

    /**
     * Gets the elements URI attr/prop value.
     * @memberof UriAwareMixin
     * @type {string}
     */
    get uri() {
      return this.getAttribute('uri');
    }

    /**
     * Sets the elements fragment attr/prop value.
     * @memberof UriAwareMixin
     * @type {string}
     */
    set fragment(newFragment) {
      this.setAttribute('fragment', newFragment);
    }

    /**
     * Gets the elements fragment attr/prop value.
     * @memberof UriAwareMixin
     * @returns {string} - The 'fragment' attribute from this element.
     */
    get fragment() {
      return this.getAttribute('fragment');
    }

    /**
     * Sets whether this component should skip creating history entries.
     * @memberof UriAwareMixin
     * @type {boolean}
     */
    set noHistory(value) {
      if (value) {
        this.setAttribute('no-history', '');
      } else {
        this.removeAttribute('no-history');
      }
    }

    /**
     * Gets whether this component skips creating history entries.
     * @memberof UriAwareMixin
     * @returns {boolean} - True if no-history attribute is present.
     */
    get noHistory() {
      return this.hasAttribute('no-history');
    }

    /**
     * Gets the current hash from the URL.
     * @memberof UriAwareMixin
     * @returns {string} - Window location Hash.
     */
    get hash() {
      return window.location.hash;
    }

    /**
     * Removed unused characters from the hash.
     * @memberof UriAwareMixin
     * @returns {string} - Window location Hash with extra characters removed.
     */
    get hashCleaned() {
      return this.hash.replace(this.configMap.regex_anchor_clean1, '').replace(this.configMap.regex_anchor_clean2, '');
    }

    /**
     * Value of the hashMap that corresponds to the current fragment.
     * @memberof UriAwareMixin
     * @type {string}
     */
    get hashValue() {
      return this.hashMap[this.fragment] || undefined;
    }

    /**
     * Set current fragments value on the hashMap.
     * @memberof UriAwareMixin
     * @type {string}
     */
    set hashValue(newValue) {
      const hashMap = this.hashMap;
      if (this.fragment) {
        hashMap[this.fragment] = newValue;
      }
      this.hashMap = hashMap;
    }

    /**
     * A Map containing fragments and their values from the URI.
     * @memberof UriAwareMixin
     * @param {object} hashMap - New hashMap to change to.
     */
    set hashMap(hashMap) {
      const uriArray = document.location.href.split('#', 2);
      const anchorString = this.makeHashString(hashMap);
      const uriString = anchorString ? uriArray[0] + anchorString : uriArray[0];

      // Use replaceState if this component has no-history attribute
      if (this.noHistory) {
        history.replaceState(null, '', uriString);
        // Manually trigger hashchange since replaceState doesn't fire it
        window.dispatchEvent(new Event('hashchange'));
      } else {
        document.location.href = uriString;
      }
    }

    /**
     * Get A Map containing fragments and their values from the URI.
     * @memberof UriAwareMixin
     * @returns {object} Object representing the current hashMap.
     */
    get hashMap() {
      const outputMap = {};
      const splitterArray = this.hashCleaned.split(this.configMap.delimit_char);

      splitterArray.map((kv) => {
        const keyValArray = kv.split(this.configMap.delimit_kv_char);
        if (keyValArray.length === 1) {
          outputMap[decodeURIComponent(keyValArray[0])] = true;
        } else if (keyValArray.length === 2) {
          outputMap[decodeURIComponent(keyValArray[0])] = decodeURIComponent(keyValArray[1]);
        }
      });

      return outputMap;
    }

    /**
     * Create a URI string given a hashMap.
     * @param {object} hashMap - Object representing the current hashMap.
     * @memberof UriAwareMixin
     * @returns {string} String form of the hashMap
     */
    makeHashString(hashMap) {
      const keyValueStrings = Object.keys(hashMap).map((key) => {
        if (!key) {
          return;
        }
        return encodeURIComponent(key) + this.configMap.delimit_kv_char + encodeURIComponent(hashMap[key]);
      });
      if (keyValueStrings.length > 0) {
        return '#!' + keyValueStrings.join(this.configMap.delimit_char);
      }
      return;
    }
  };

export { UriAwareMixin };
